import {
  Card, Flexbox, ListManager, PageHeader, Styles,
} from '@/components'
import { Button } from '@/components/styles'
import useAxiosRequest from '@/hooks/useAxiosRequest'
import usePageTitle from '@/hooks/usePageTitle'
import stalls from '@/services/api/stalls'
import AddAnimal from '@/views/app/Stalls/components/AddAnimal'
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import FormStall from '../components/Form'
import AnimalCard from './components/AnimalCard'
import DropZone from './styles'

function occupancy(stall) {
  const count = stall.animals?.length || 0
  if (stall.capacity) return `${count} de ${stall.capacity}`
  return String(count)
}

function sameId(left, right) {
  return String(left) === String(right)
}

function withoutPending(animal) {
  const next = { ...animal }
  delete next.pendingChange
  delete next.pendingFromStallId
  return next
}

function findPlacement(rows, animalId) {
  return rows.reduce((found, stall) => {
    if (found) return found
    const current = (stall.animals || []).find((item) => sameId(item.id, animalId))
    if (!current) return null
    return { stallId: stall.id, animal: current }
  }, null)
}

function savedOrigin(placement) {
  if (!placement) return undefined
  if (placement.animal.pendingChange === 'add') return placement.animal.pendingFromStallId
  return placement.stallId
}

function placeAnimal(rows, stallId, animal) {
  return rows.map((item) => {
    const animals = (item.animals || []).filter((current) => !sameId(current.id, animal.id))
    if (sameId(item.id, stallId)) return { ...item, animals: [...animals, animal] }
    if (animals.length !== (item.animals || []).length) return { ...item, animals }
    return item
  })
}

function pendingTransfers(rows) {
  return rows.reduce((transfers, stall) => (
    (stall.animals || []).reduce((next, animal) => {
      if (animal.pendingChange === 'add') {
        return [...next, { animalId: animal.id, stallId: stall.id }]
      }
      if (animal.pendingChange === 'remove') {
        return [...next, { animalId: animal.id, stallId: null }]
      }
      return next
    }, transfers)
  ), [])
}

function StallSection({
  stall, onAnimalStaged, onAnimalRemoved, onAnimalDropped, onStallUpdated, onAnimalUpdated,
}) {
  const [adding, setAdding] = useState(false)
  const [over, setOver] = useState(false)
  const animals = stall.animals || []

  function onDragOver(event) {
    event.preventDefault()
    if (!over) setOver(true)
  }

  function onDragLeave(event) {
    if (event.currentTarget.contains(event.relatedTarget)) return
    setOver(false)
  }

  function onDrop(event) {
    event.preventDefault()
    setOver(false)
    const animalId = event.dataTransfer.getData('text/plain')
    if (animalId) onAnimalDropped(stall.id, animalId)
  }

  return (
    <Card.Base
      className="mb-2"
      style={over ? { background: '#f1f3f5' } : undefined}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <Card.Header>
        <Flexbox.Columns className="align-center">
          <Flexbox.Column>
            <div className="h5 mb-0">{stall.name}</div>
            <div className="text-small">
              <cite>{`Código ${stall.code} · ${occupancy(stall)} animais`}</cite>
            </div>
          </Flexbox.Column>
          <Flexbox.Column className="col-auto">
            <div className="d-flex">
              <Link
                className="btn btn-sm mr-2"
                to={{
                  pathname: `/app/stalls/${stall.id}/history`,
                  state: { stall: { name: stall.name, code: stall.code } },
                }}
              >
                <i className="fas fa-history" />
                &nbsp;Histórico
              </Link>
              <FormStall
                stall={stall}
                onSuccess={onStallUpdated}
                ButtonComponent={({ onClick }) => (
                  <Button onClick={onClick} className="btn-sm mr-2">
                    <i className="fas fa-edit" />
                    &nbsp;Editar
                  </Button>
                )}
              />
              <Button className="btn-sm btn-primary" onClick={() => setAdding(true)}>
                + Animal
              </Button>
            </div>
          </Flexbox.Column>
        </Flexbox.Columns>
      </Card.Header>
      <Card.Body>
        <DropZone $over={over}>
          {
            animals.length
              ? (
                <Flexbox.Columns>
                  {
                    animals.map((animal) => (
                      <Flexbox.Column className="col-4 col-md-6 col-sm-12" key={String(animal.id)}>
                        <AnimalCard
                          animal={animal}
                          onRemoved={(removed) => onAnimalRemoved(stall.id, removed)}
                          onUpdated={onAnimalUpdated}
                        />
                      </Flexbox.Column>
                    ))
                  }
                </Flexbox.Columns>
              )
              : <div className="text-gray">Nenhum animal nesta baia</div>
          }
        </DropZone>
      </Card.Body>
      {
        adding && (
          <AddAnimal
            onAdded={(animal) => onAnimalStaged(stall.id, animal)}
            onClose={() => setAdding(false)}
          />
        )
      }
    </Card.Base>
  )
}

StallSection.propTypes = {
  stall: PropTypes.objectOf(Object).isRequired,
  onAnimalStaged: PropTypes.func.isRequired,
  onAnimalRemoved: PropTypes.func.isRequired,
  onAnimalDropped: PropTypes.func.isRequired,
  onStallUpdated: PropTypes.func.isRequired,
  onAnimalUpdated: PropTypes.func.isRequired,
}

function Dashboard({ title }) {
  usePageTitle({ title })
  const {
    data, loading, error, setData,
  } = useAxiosRequest(stalls.all)
  const [saving, setSaving] = useState(false)
  const rows = Array.isArray(data) ? data : []
  const animalCount = rows.reduce((sum, stall) => sum + (stall.animals?.length || 0), 0)
  const transfers = pendingTransfers(rows)

  function onStallAdded(stall) {
    setData([{ ...stall, animals: stall.animals || [] }, ...rows])
  }

  function onStallUpdated(updated) {
    setData(rows.map((item) => (
      sameId(item.id, updated.id)
        ? {
          ...item,
          name: updated.name,
          code: updated.code,
          capacity: updated.capacity,
        }
        : item
    )))
  }

  function onAnimalUpdated(updated) {
    setData(rows.map((stall) => ({
      ...stall,
      animals: (stall.animals || []).map((current) => (
        sameId(current.id, updated.id)
          ? {
            ...current,
            ...updated,
            pendingChange: current.pendingChange,
            pendingFromStallId: current.pendingFromStallId,
          }
          : current
      )),
    })))
  }

  function onAnimalStaged(stallId, animal) {
    const placement = findPlacement(rows, animal.id)
    if (placement && sameId(placement.stallId, stallId)) {
      if (placement.animal.pendingChange === 'remove') {
        setData(rows.map((item) => (
          sameId(item.id, stallId)
            ? {
              ...item,
              animals: (item.animals || []).map((current) => (
                sameId(current.id, animal.id) ? withoutPending(current) : current
              )),
            }
            : item
        )))
        return
      }
      toast.error(`${animal.name} já está nesta baia`)
      return
    }

    const origin = savedOrigin(placement)
    const source = withoutPending(placement ? placement.animal : animal)
    const next = origin && sameId(origin, stallId)
      ? source
      : {
        ...source,
        pendingChange: 'add',
        ...(origin ? { pendingFromStallId: origin } : {}),
      }
    setData(placeAnimal(rows, stallId, next))
  }

  function onAnimalDropped(stallId, animalId) {
    const placement = findPlacement(rows, animalId)
    if (!placement || sameId(placement.stallId, stallId)) return
    onAnimalStaged(stallId, placement.animal)
  }

  function onAnimalRemoved(stallId, animal) {
    if (animal.pendingChange === 'add') {
      const restored = withoutPending(animal)
      setData(rows.map((item) => {
        if (sameId(item.id, stallId)) {
          return {
            ...item,
            animals: (item.animals || []).filter((current) => !sameId(current.id, animal.id)),
          }
        }
        if (animal.pendingFromStallId && sameId(item.id, animal.pendingFromStallId)) {
          const animals = item.animals || []
          if (animals.some((current) => sameId(current.id, animal.id))) return item
          return { ...item, animals: [...animals, restored] }
        }
        return item
      }))
      return
    }

    if (animal.pendingChange === 'remove') {
      setData(rows.map((item) => (
        sameId(item.id, stallId)
          ? {
            ...item,
            animals: (item.animals || []).map((current) => (
              sameId(current.id, animal.id) ? withoutPending(current) : current
            )),
          }
          : item
      )))
      return
    }

    setData(rows.map((item) => (
      sameId(item.id, stallId)
        ? {
          ...item,
          animals: (item.animals || []).map((current) => (
            sameId(current.id, animal.id) ? { ...current, pendingChange: 'remove' } : current
          )),
        }
        : item
    )))
  }

  async function saveChanges() {
    try {
      setSaving(true)
      const { data: saved } = await stalls.transferAnimals(transfers)
      const returned = Array.isArray(saved) ? saved.filter(Boolean) : []
      setData(rows.map((item) => {
        const fresh = returned.find((stall) => sameId(stall.id, item.id))
        return fresh ? { ...fresh, animals: fresh.animals || [] } : item
      }))
      toast.success('Alterações salvas')
    } catch (e) {
      toast.error(e?.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Styles.Container className="grid-xl">
      <PageHeader
        title="Painel das baias"
        icon="fas fa-th-large"
        subtitle={loading ? '' : `${rows.length} baias · ${animalCount} animais`}
        rightAction={() => (
          <>
            {
              transfers.length > 0 && (
                <Button
                  onClick={saveChanges}
                  className={`btn-success mr-2 ${saving ? 'loading' : ''}`}
                  disabled={saving}
                >
                  <i className="fas fa-save" />
                  &nbsp;Salvar alterações
                </Button>
              )
            }
            <FormStall
              onSuccess={onStallAdded}
              ButtonComponent={({ onClick }) => (
                <Button onClick={onClick} className="btn-primary">
                  <i className="fas fa-plus" />
                  &nbsp;Nova baia
                </Button>
              )}
            />
          </>
        )}
      />
      <ListManager
        count={rows.length}
        loading={loading}
        error={error}
        emptyMessage="Nenhuma baia cadastrada"
        emptyIcon="fas fa-warehouse"
      >
        {
          rows.map((stall) => (
            <StallSection
              key={String(stall.id)}
              stall={stall}
              onAnimalStaged={onAnimalStaged}
              onAnimalRemoved={onAnimalRemoved}
              onAnimalDropped={onAnimalDropped}
              onStallUpdated={onStallUpdated}
              onAnimalUpdated={onAnimalUpdated}
            />
          ))
        }
      </ListManager>
    </Styles.Container>
  )
}

Dashboard.propTypes = {
  title: PropTypes.string.isRequired,
}

export default Dashboard
